import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';
import { getClassRoster, getMemoCounts, getRosterNotes, getRosterStudentRecords, getStudentNotes } from '../../api/classRosterApi';
import { trackClassRosterEvent } from '../../utils/eventTracker';
import { semesterLabel } from '../../utils/classRosterManage';
import {
  buildClassEvidenceHandoff,
  buildRosterSaenggibuHandoff,
  hasScoringRecords,
  scoringEvidence,
} from '../../utils/studentRecordsHandoff';
import {
  SINGLE_CATEGORIES,
  buildEvidenceItems,
  buildMemoPrompt,
  isSemesterEnd,
  memosForCategory,
  profileForCategory,
} from '../../utils/studentMemos';
import ClassMemoGrid from './ClassMemoGrid';
import ClassWriteSection from './ClassWriteSection';
import RosterClassCheck from './RosterClassCheck';
import StudentNotes from './StudentNotes';
import './ClassesPage.css';
import './ClassStudentsPage.css';

const KIND_LABELS = { scoring: '채점', saenggibu: '생기부', submission: '제출' };
const EMPTY_COUNTS = { scoring: 0, saenggibu: 0, submission: 0 };

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.getMonth() + 1}월 ${date.getDate()}일`;
};

const formatScore = (item) => {
  if (item.blank) return '빈 답안';
  if (item.score === null || item.score === undefined) return '';
  return item.max_score ? `${item.score}/${item.max_score}점` : `${item.score}점`;
};

const recordCount = student => (student.items || []).length;

const mergeStudents = (rosterStudents = [], recordStudents = null) => {
  const bySid = new Map((recordStudents || []).map(item => [item.sid, item]));
  return rosterStudents.map(item => ({
    ...item,
    counts: bySid.get(item.sid)?.counts || EMPTY_COUNTS,
    items: bySid.get(item.sid)?.items || [],
  }));
};

const Skeleton = () => (
  <div className="class-students-skeleton" aria-busy="true" aria-label="불러오는 중">
    <span className="is-title" />
    <span className="is-cta" />
    {[0, 1, 2, 3].map(key => <span key={key} className="is-row" />)}
  </div>
);

const RecordCard = ({ item }) => {
  const score = item.kind === 'scoring' ? formatScore(item) : '';
  return (
    <li className="class-students-record">
      <div className="class-students-record-top">
        <span className={`class-students-kind is-${item.kind}`}>{KIND_LABELS[item.kind]}</span>
        <span className="class-students-date">{formatDate(item.date)}</span>
      </div>
      <div className="class-students-record-title">
        {item.title}
        {item.subtitle && <span className="class-students-record-sub">{item.subtitle}</span>}
        {score && <span className="class-students-record-score">{score}</span>}
      </div>
      {item.line && <p className="class-students-record-line">{item.line}</p>}
    </li>
  );
};

/** 학생 화면 '생기부 쓰기': 영역을 고르면 그 영역 메모(와 고르면 채점 결과)를 날짜와 함께 단일 생성으로 넘긴다. */
const WriteSection = ({ notes, notesStatus, scoring, recordsReady, scoringCount, starting, onStart, onStartScoring }) => {
  const [category, setCategory] = useState('행동발달');
  const [withScoring, setWithScoring] = useState(null);
  const includeScoring = scoring.length > 0 && (withScoring === null ? category === '교과' : withScoring);
  const memoCount = memosForCategory(notes?.memos || [], category).length;
  const evidence = memoCount + (includeScoring ? scoring.length : 0);
  const label = SINGLE_CATEGORIES.find(item => item.category === category)?.label || '생기부';
  const loading = notesStatus === 'loading';

  return (
    <section className="class-students-write" data-testid="student-write">
      <strong className="class-students-write-title">생기부 쓰기</strong>
      <div className="class-students-chips" role="group" aria-label="영역">
        {SINGLE_CATEGORIES.map(item => (
          <button
            key={item.category}
            type="button"
            aria-pressed={category === item.category}
            className={category === item.category ? 'is-active' : ''}
            onClick={() => setCategory(item.category)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <p className="class-students-write-count" data-testid="student-write-count">
        {loading ? '메모 불러오는 중…' : evidence > 0
          ? `근거 ${evidence}개 · 메모 ${memoCount}${includeScoring ? ` · 채점 ${scoring.length}` : ''}`
          : '이 영역 메모가 없어요. 입력칸에 직접 적어도 돼요.'}
      </p>
      {recordsReady && scoring.length > 0 && (
        <label className="class-students-write-toggle">
          <input type="checkbox" checked={includeScoring} onChange={event => setWithScoring(event.target.checked)} />
          채점 결과 {scoring.length}개 함께 넣기
        </label>
      )}
      <div className="class-students-write-actions">
        <button
          type="button"
          className="classes-btn-primary"
          disabled={loading}
          onClick={() => onStart({ category, includeScoring })}
        >
          {loading ? '메모 불러오는 중…' : `${label} 쓰기`}
        </button>
        {scoringCount > 0 && (
          <button type="button" className="classes-btn-secondary" disabled={starting} onClick={onStartScoring}>
            {starting ? '불러오는 중…' : `채점 기록 ${scoringCount}개로 교과세특 쓰기`}
          </button>
        )}
      </div>
    </section>
  );
};

const ClassStudentsPage = () => {
  const { rosterId, sid } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [rosterState, setRosterState] = useState({ loading: true, error: '', roster: null });
  const [records, setRecords] = useState({ status: 'loading', data: null });
  const [starting, setStarting] = useState(false);
  const [notes, setNotes] = useState(null);
  const [notesStatus, setNotesStatus] = useState('idle');
  const [notesReload, setNotesReload] = useState(0);
  const notesLoadRef = useRef(0);
  const [memoCounts, setMemoCounts] = useState(null);
  const [classNotes, setClassNotes] = useState({ status: 'idle', data: null });
  const [classNotesReload, setClassNotesReload] = useState(0);
  const viewedRef = useRef('');

  const loadRoster = useCallback(async () => {
    setRosterState(prev => ({ ...prev, loading: true, error: '' }));
    try {
      setRosterState({ loading: false, error: '', roster: await getClassRoster(rosterId) });
    } catch (error) {
      setRosterState({
        loading: false,
        error: error.status === 404 ? '명단을 찾지 못했어요. 학급 관리에서 다시 골라 주세요.' : '명단을 불러오지 못했어요. 잠시 후 다시 시도해주세요.',
        roster: null,
      });
    }
  }, [rosterId]);

  // 반 전체 기록(채점 문서 최대 1,500개)은 느릴 수 있어 따로 불러온다. 명단·메모는 먼저 보인다.
  const loadRecords = useCallback(async () => {
    setRecords(prev => ({ ...prev, status: 'loading' }));
    try {
      setRecords({ status: 'ready', data: await getRosterStudentRecords(rosterId) });
    } catch (error) {
      setRecords({ status: 'error', data: null });
    }
  }, [rosterId]);

  const uid = currentUser?.uid;
  useEffect(() => {
    if (!uid) return;
    loadRoster();
    loadRecords();
  }, [uid, loadRoster, loadRecords]);

  const roster = rosterState.roster;
  const recordsReady = records.status === 'ready';
  const students = useMemo(
    () => mergeStudents(roster?.students || [], records.data?.students),
    [roster, records.data],
  );
  const student = sid ? students.find(item => item.sid === sid) : null;

  useEffect(() => {
    setNotes(null);
    if (!uid || !sid) {
      setNotesStatus('idle');
      return undefined;
    }
    let alive = true;
    const loadId = notesLoadRef.current + 1;
    notesLoadRef.current = loadId;
    setNotesStatus('loading');
    getStudentNotes(rosterId, sid)
      .then((data) => {
        if (!alive) return;
        setNotes({ ...data, loadId });
        setNotesStatus('ready');
      })
      .catch(() => { if (alive) setNotesStatus('error'); });
    return () => { alive = false; };
  }, [uid, rosterId, sid, notesReload]);

  // 반 화면: 학생마다 메모·고정 정보를 한 번에 불러온다. 학생 칸 메모 수는 처음에만 여기서 채우고,
  // 그 뒤로는 칸이 남기기·되돌리기로 직접 고친다(다시 불러온 결과가 방금 고친 수를 덮지 않게).
  useEffect(() => {
    if (!uid || sid) return undefined;
    let alive = true;
    const first = classNotesReload === 0;
    setClassNotes(prev => (prev.data ? prev : { status: 'loading', data: null }));
    getRosterNotes(rosterId)
      .then((data) => {
        if (!alive) return;
        setClassNotes({ status: 'ready', data });
        if (first) setMemoCounts(Object.fromEntries(Object.entries(data).map(([key, item]) => [key, (item?.memos || []).length])));
      })
      .catch(() => {
        if (!alive) return;
        setClassNotes(prev => ({ ...prev, status: prev.data ? 'ready' : 'error' }));
        if (!first) return;
        getMemoCounts(rosterId)
          .then((counts) => { if (alive) setMemoCounts(counts); })
          .catch(() => { if (alive) setMemoCounts(null); });
      });
    return () => { alive = false; };
  }, [uid, rosterId, sid, classNotesReload]);

  // 학생 화면은 같은 컴포넌트라 반 화면으로 돌아와도 다시 마운트되지 않는다. 그 사이 남긴 메모까지 칸 수를 다시 맞춘다.
  useEffect(() => {
    if (sid) setClassNotesReload(0);
  }, [sid]);

  const handleMemoCountsChange = useCallback((update) => {
    setMemoCounts(update);
    setClassNotesReload(n => n + 1);
  }, []);

  // 메모 저장 응답이 늦게 오면 그 사이 다른 학생(또는 다시 불러온 메모)에 섞이지 않게, 불러온 그 메모에만 반영한다.
  const notesLoadId = notes?.loadId;
  const updateNotes = useCallback((update) => {
    setNotes((prev) => {
      if (!prev || prev.loadId !== notesLoadId) return prev;
      const next = typeof update === 'function' ? update(prev) : update;
      return { ...next, loadId: prev.loadId };
    });
  }, [notesLoadId]);

  const memoGap = memoCounts && isSemesterEnd()
    ? students.filter(item => !memoCounts[item.sid]).length
    : 0;

  useEffect(() => {
    if (!roster || !recordsReady) return;
    const key = `${roster.id}:${sid || ''}`;
    if (viewedRef.current === key) return;
    viewedRef.current = key;
    if (sid) {
      if (student) trackClassRosterEvent('student_record_opened', { roster_id: roster.id, record_count: recordCount(student) });
      return;
    }
    trackClassRosterEvent('student_records_viewed', {
      roster_id: roster.id,
      student_count: students.length,
      linked_count: students.filter(item => recordCount(item) > 0).length,
      record_count: students.reduce((sum, item) => sum + recordCount(item), 0),
    });
  }, [roster, recordsReady, sid, student, students]);

  const startSaenggibu = async (targets, scope) => {
    if (starting || targets.length === 0) return;
    setStarting(true);
    try {
      const batchRestore = await buildRosterSaenggibuHandoff({ roster, students: targets });
      if (!batchRestore) {
        showToast('생기부로 넘길 채점 내용이 없어요. 빈 답안이나 오류 결과는 넘기지 않아요.', 'info', 6000);
        return;
      }
      trackClassRosterEvent('student_saenggibu_start', {
        roster_id: roster.id,
        scope,
        student_count: batchRestore.students.length,
        record_count: batchRestore.sourceFileMeta?.scoring_record_count || 0,
      });
      navigate('/create-saenggibu', { state: { batchRestore } });
    } catch (error) {
      showToast(error.message || '채점 기록을 불러오지 못했어요. 다시 시도해주세요.', 'error');
    } finally {
      setStarting(false);
    }
  };

  // 숫자 줄은 화면에 있던 메모로 보여 주고, 넘길 때는 방금 남긴 메모까지 들어가게 다시 불러온다.
  const startClass = async ({ category, includeScoring }) => {
    if (starting) return;
    setStarting(true);
    try {
      const notesBySid = await getRosterNotes(roster.id);
      const batchRestore = await buildClassEvidenceHandoff({ roster, students, notesBySid, category, includeScoring });
      if (!batchRestore) {
        showToast('이 영역에 넣을 메모나 채점 결과가 없어요.', 'info', 6000);
        return;
      }
      const meta = batchRestore.sourceFileMeta;
      trackClassRosterEvent('student_saenggibu_start', {
        roster_id: roster.id,
        scope: 'class',
        category,
        student_count: meta.student_count,
        missing_count: meta.skipped_count,
        memo_count: meta.memo_count,
        scoring_count: meta.scoring_student_count,
        record_count: meta.scoring_record_count || 0,
      });
      navigate('/create-saenggibu', { state: { batchRestore } });
    } catch (error) {
      showToast(error.message || '기록을 불러오지 못했어요. 다시 시도해주세요.', 'error');
    } finally {
      setStarting(false);
    }
  };

  const startSingle = ({ category, includeScoring }) => {
    if (notesStatus === 'loading') return;
    const allMemos = notes?.memos || [];
    const memos = memosForCategory(allMemos, category);
    const scoring = includeScoring ? scoringEvidence(student) : [];
    const keptProfile = profileForCategory(notes?.profile, category);
    const options = { category, names: [student.name, ...(student.aliases || [])] };
    trackClassRosterEvent('student_single_start', {
      roster_id: roster.id,
      memo_count: memos.length,
      category,
      scoring_count: scoring.length,
      other_year_count: notes?.otherYearMemos?.length || 0,
    });
    navigate('/create-saenggibu', {
      state: {
        category,
        prompt: buildMemoPrompt({ profile: keptProfile, memos, scoring }, options),
        rosterStudent: { rosterId: roster.id, rosterLabel: roster.label, sid: student.sid, name: student.name, memoCount: memos.length },
        memoEvidence: {
          version: 1,
          schoolYear: roster.school_year,
          category,
          items: buildEvidenceItems({ memos, scoring }, options),
          excluded: {
            otherYear: notes?.otherYearMemos?.length || 0,
            otherArea: allMemos.length - memos.length,
          },
        },
      },
    });
  };

  const classPath = `/classes/${encodeURIComponent(rosterId)}`;
  const studentPath = useCallback(item => `${classPath}/students/${encodeURIComponent(item.sid)}`, [classPath]);
  const title = student ? `${student.name} 기록` : roster ? `${roster.label} 학생별 기록` : '학생별 기록';
  const recordsError = records.status === 'error' && (
    <p className="class-students-notes-error" role="alert">
      채점·생기부 기록을 불러오지 못했어요.
      <button type="button" className="class-students-inline-btn" onClick={loadRecords}>다시 불러오기</button>
    </p>
  );

  return (
    <div className="classes-page class-students-page">
      <Helmet>
        <title>{title} | 왓퀴즈</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <nav className="class-students-crumb" aria-label="현재 위치">
        <Link to="/classes">학급 관리</Link>
        {sid && roster && (
          <>
            <span aria-hidden="true">›</span>
            <Link to={classPath}>{roster.label}</Link>
          </>
        )}
      </nav>

      {(rosterState.loading || !currentUser) && !rosterState.error && <Skeleton />}

      {rosterState.error && (
        <div className="class-students-message" role="alert">
          <strong>{rosterState.error}</strong>
          <button type="button" className="classes-btn-secondary" onClick={loadRoster}>다시 불러오기</button>
        </div>
      )}

      {!rosterState.loading && roster && !sid && (
        <>
          <header className="class-students-head">
            <h1>{roster.label}</h1>
            <p>
              {roster.school_year}학년도 {semesterLabel(roster.semester)} · 학생 {students.length}명
            </p>
          </header>

          <ClassMemoGrid
            roster={roster}
            students={students}
            memoCounts={memoCounts}
            onMemoCountsChange={handleMemoCountsChange}
            studentPath={studentPath}
          />
          {memoGap > 0 && (
            <p className="class-students-memo-gap" data-testid="memo-gap">메모 없는 학생 {memoGap}명 · 학기 말 전에 한 줄씩 남겨 두면 생기부 쓸 때 편해요</p>
          )}

          <ClassWriteSection
            students={students}
            notesBySid={classNotes.data}
            notesStatus={classNotes.status}
            recordsStatus={records.status}
            starting={starting}
            onStart={startClass}
            onRetry={() => setClassNotesReload(n => n + 1)}
          />
          {recordsError}

          <RosterClassCheck rosterId={roster.id} students={students} classPath={classPath} />

          <p className="class-students-note">
            {roster.school_year}학년도 기록 중 이름이 명단과 같거나 저장한 별칭인 기록만 모아요.
            {records.data?.truncated && ' 기록이 많아 최근 기록부터 보여 줘요.'}
          </p>
        </>
      )}

      {!rosterState.loading && roster && sid && !student && (
        <div className="class-students-message" role="alert">
          <strong>이 학생을 명단에서 찾지 못했어요</strong>
          <Link className="classes-btn-secondary class-students-link-btn" to={classPath}>반으로 돌아가기</Link>
        </div>
      )}

      {!rosterState.loading && student && (
        <>
          <header className="class-students-head">
            <h1>{student.name}</h1>
            <p>{student.number}번{recordsReady ? ` · 기록 ${recordCount(student)}개` : ''}</p>
          </header>

          {notes && (
            <StudentNotes
              rosterId={roster.id}
              sid={student.sid}
              notes={notes}
              evidence={student.items.filter(item => item.kind === 'scoring')}
              onChange={updateNotes}
            />
          )}
          {notesStatus === 'loading' && !notes && <div className="class-students-notes-skeleton" aria-busy="true" aria-label="메모 불러오는 중" />}
          {notesStatus === 'error' && (
            <p className="class-students-notes-error" role="alert">
              메모를 불러오지 못했어요.
              <button type="button" className="class-students-inline-btn" onClick={() => setNotesReload(n => n + 1)}>다시 불러오기</button>
            </p>
          )}

          <WriteSection
            key={student.sid}
            notes={notes}
            notesStatus={notesStatus}
            scoring={scoringEvidence(student)}
            recordsReady={recordsReady}
            scoringCount={hasScoringRecords(student) ? student.counts.scoring : 0}
            starting={starting}
            onStart={startSingle}
            onStartScoring={() => startSaenggibu([student], 'one')}
          />
          {recordsError}

          {student.items.some(item => item.kind !== 'scoring') && (
            <>
              <h2 className="class-students-section">모은 기록</h2>
              <ul className="class-students-records" data-testid="class-student-records">
                {student.items.filter(item => item.kind !== 'scoring')
                  .map(item => <RecordCard key={`${item.kind}-${item.record_id}-${item.date}`} item={item} />)}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ClassStudentsPage;
